import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { AccountStatusService } from '../core/services/account-status.service';
import { AnalyticsService } from '../core/services/analytics.service';
import { ToastService } from '../core/services/toast.service';
import { CreateEventSheetComponent } from './create-event-sheet.component';

/**
 * Opens the create-event sheet, with the guards and instrumentation that have
 * to travel with it.
 *
 * HomeComponent.openCreateEvent() grew all of this inline: plan-state checks,
 * the toast that explains a block, the Meta/GA/PostHog calls, the localStorage
 * reset, and the mobile-vs-desktop dialog config. Any second entry point that
 * reimplemented it would drift — the create funnel would end up measuring two
 * different things, and a plan-expired user could slip past the gate from
 * whichever surface forgot the check.
 *
 * Lives beside the sheet rather than in core/services so that core does not
 * take a dependency on a feature component.
 *
 * HomeComponent still has its own copy and should be migrated onto this; it was
 * left alone here only to avoid a conflicting edit in a file under active
 * development elsewhere.
 */
@Injectable({ providedIn: 'root' })
export class CreateEventLauncherService {

    private static readonly BLOCKED_MESSAGES: Record<string, string> = {
        live: "There's already a live event going on. Stop it before you create a new one.",
        plan_expired: "Time to renew! Then you're back to creating events.",
        plan_grace: 'Renew your plan to keep creating events.',
    };

    /** Guards against a second sheet when a click lands twice. */
    private openRef: MatDialogRef<CreateEventSheetComponent> | null = null;

    constructor(
        private dialog: MatDialog,
        private accountStatus: AccountStatusService,
        private toastService: ToastService,
        private analytics: AnalyticsService,
    ) { }

    /** Matches the breakpoint HomeComponent uses. */
    private get isMobile(): boolean {
        return window.innerWidth <= 768;
    }

    /**
     * Why creation cannot proceed, or null when it can.
     *
     * Plan state is checked before anything a caller passes in: an expired plan
     * is the reason the user actually has to act on, and sending them to
     * "finish your live event first" only ends in a renewal prompt anyway.
     *
     * `extra` exists because some blocks are caller-specific — HomeComponent
     * knows whether one of the user's events is currently live; nothing in this
     * service does.
     */
    blockedReason(extra?: string | null): string | null {
        const level = this.accountStatus.snapshot?.plan?.alert_level;
        if (level === 'expired') return 'plan_expired';
        if (level === 'grace') return 'plan_grace';
        return extra ?? null;
    }

    /**
     * Opens the sheet, or explains why it can't. Returns null when blocked or
     * when a sheet is already open, so callers can tell whether anything opened.
     */
    launch(
        source: string,
        extraBlockedReason?: string | null,
    ): MatDialogRef<CreateEventSheetComponent> | null {

        // Guards run before the pixels. Blocked clicks were never going to open
        // anything, so counting them as create_event_clicked would put dead ends
        // at the top of the creation funnel.
        const blocked = this.blockedReason(extraBlockedReason);
        if (blocked) {
            this.toastService.showToast(CreateEventLauncherService.BLOCKED_MESSAGES[blocked], 4000);
            this.analytics.capture('create_event_blocked', { source, reason: blocked });
            return null;
        }

        // Optional: absent on surfaces where the pixel/tag never loaded.
        (window as any).fbq?.('trackCustom', 'pwa_start_create_event');
        (window as any).gtag?.('event', 'create_event', {
            event_category: 'user_action',
            event_label: 'Event Page',
            value: 1,
        });
        this.analytics.capture('create_event_clicked', { source });

        localStorage.removeItem('formData');
        localStorage.removeItem('showEventDetail');

        if (this.openRef) return null;

        // Same sheet on both surfaces: mobile pins it to the bottom edge full
        // width, desktop centres it as a fixed-width dialog.
        this.openRef = this.isMobile
            ? this.dialog.open(CreateEventSheetComponent, {
                hasBackdrop: true,
                backdropClass: 'custom-backdrop',
                panelClass: 'create-sheet-panel',
                position: { bottom: '0' },
                width: '100vw',
                maxWidth: '100vw',
                autoFocus: false,
            })
            : this.dialog.open(CreateEventSheetComponent, {
                hasBackdrop: true,
                backdropClass: 'custom-backdrop',
                panelClass: 'create-sheet-panel-desktop',
                width: '480px',
                maxWidth: '90vw',
                autoFocus: false,
            });

        this.openRef.afterClosed().subscribe(() => (this.openRef = null));
        return this.openRef;
    }
}
